<template>
  <el-dialog
    v-model="visible"
    append-to-body
    :close-on-click-modal="false"
    :title="currentNotice?.noticeTitle || '新公告通知'"
    width="680px"
    @closed="handleClosed"
  >
    <div v-if="currentNotice" v-loading="detailLoading" class="global-notice">
      <div class="notice-banner">
        <span class="notice-icon"><el-icon><Bell /></el-icon></span>
        <div>
          <span>NEW NOTICE</span>
          <strong>您收到一条新的{{ getNoticeTypeLabel(currentNotice.noticeType) }}</strong>
        </div>
      </div>

      <div class="notice-meta">
        <el-tag :type="currentNotice.noticeType === '1' ? 'warning' : 'success'" effect="plain">
          {{ getNoticeTypeLabel(currentNotice.noticeType) }}
        </el-tag>
        <span>{{ currentNotice.createBy || '系统管理员' }}</span>
        <span>{{ currentNotice.createTime || '' }}</span>
      </div>

      <div class="notice-content" v-html="currentNotice.noticeContent || '暂无公告内容'"></div>
    </div>

    <template #footer>
      <el-button type="primary" @click="visible = false">我知道了</el-button>
    </template>
  </el-dialog>
</template>

<script setup lang="ts">
import { onBeforeUnmount, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { Bell } from '@element-plus/icons-vue'
import { getNotice, listNoticeTop, markNoticeRead } from '@/api/notice'
import type { EntityId, NoticeItem } from '@/types/api'
import { getToken, getUser } from '@/utils/auth'

const route = useRoute()
const visible = ref(false)
const detailLoading = ref(false)
const currentNotice = ref<NoticeItem | null>(null)
const noticeQueue: NoticeItem[] = []
const displayedNoticeIds = new Set<EntityId>()
let checking = false
let closedTimer: number | undefined
let currentUserKey = ''
let detailRequestId = 0

function getNoticeTypeLabel(noticeType?: string): string {
  return noticeType === '1' ? '通知' : '公告'
}

function isPending(noticeId: EntityId): boolean {
  return currentNotice.value?.noticeId === noticeId
    || noticeQueue.some((notice) => notice.noticeId === noticeId)
}

function showNextNotice() {
  if (visible.value || !noticeQueue.length) return

  const notice = noticeQueue.shift()
  if (!notice) return
  currentNotice.value = notice
  visible.value = true
  detailLoading.value = true
  const requestId = ++detailRequestId

  getNotice(notice.noticeId)
    .then((response) => {
      if (requestId === detailRequestId && currentNotice.value?.noticeId === notice.noticeId) {
        currentNotice.value = response?.data || notice
      }
    })
    .catch(() => {
      currentNotice.value = currentNotice.value || notice
    })
    .finally(() => {
      if (requestId === detailRequestId) {
        detailLoading.value = false
      }
    })

  markNoticeRead(notice.noticeId).then(() => {
    window.dispatchEvent(new CustomEvent('ruoyi-notice-read', {
      detail: { noticeId: notice.noticeId }
    }))
  }).catch(() => undefined)
}

async function checkNewNotices() {
  if (!getToken() || checking) return

  const user = getUser()
  const userKey = String(user?.userId || user?.id || user?.username || '')

  if (currentUserKey !== userKey) {
    currentUserKey = userKey
    displayedNoticeIds.clear()
    noticeQueue.splice(0)
  }

  checking = true
  try {
    const response = await listNoticeTop()
    const notices = (response?.data || [])
      .filter((notice) => !notice.isRead && String(notice.status ?? '0') === '0')
      .filter((notice) => !displayedNoticeIds.has(notice.noticeId) && !isPending(notice.noticeId))
      .sort((left, right) => Number(left.noticeId) - Number(right.noticeId))

    notices.forEach((notice) => {
      displayedNoticeIds.add(notice.noticeId)
      noticeQueue.push(notice)
    })
    showNextNotice()
  } catch {
    return
  } finally {
    checking = false
  }
}

function handleClosed() {
  detailRequestId += 1
  detailLoading.value = false
  currentNotice.value = null
  if (closedTimer !== undefined) window.clearTimeout(closedTimer)
  closedTimer = window.setTimeout(showNextNotice, 120)
}

const stopRouteWatch = watch(
  () => route.fullPath,
  () => checkNewNotices().catch(() => undefined),
  { immediate: true }
)

onBeforeUnmount(() => {
  stopRouteWatch()
  if (closedTimer !== undefined) window.clearTimeout(closedTimer)
})
</script>

<style scoped>
.global-notice {
  min-height: 220px;
}

.notice-banner {
  display: flex;
  padding: 18px;
  border-radius: 16px;
  background: linear-gradient(110deg, #f0f2ff, #f8f6ff);
  align-items: center;
  gap: 13px;
}

.notice-icon {
  display: grid;
  width: 46px;
  height: 46px;
  color: #ffffff;
  border-radius: 14px;
  background: linear-gradient(135deg, #596deb, #7559dd);
  box-shadow: 0 10px 20px rgba(92, 99, 214, 0.2);
  place-items: center;
}

.notice-banner div > span,
.notice-banner strong {
  display: block;
}

.notice-banner div > span {
  color: #7b85c4;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.14em;
}

.notice-banner strong {
  margin-top: 6px;
  color: #30384f;
  font-size: 15px;
}

.notice-meta {
  display: flex;
  margin: 20px 0;
  color: #969dae;
  font-size: 12px;
  align-items: center;
  gap: 12px;
}

.notice-content {
  min-height: 120px;
  color: #4d5569;
  font-size: 14px;
  line-height: 1.9;
  white-space: pre-wrap;
  word-break: break-word;
}

@media (max-width: 620px) {
  .notice-meta {
    flex-wrap: wrap;
  }
}
</style>
